import React, {forwardRef, useEffect, useState} from 'react';
import {Card, CardBody, CardHeader, Spinner} from '@goproblems/ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faChevronLeft,
    faChevronRight,
    faCodeBranch,
    faPlay,
    faRotateLeft,
    faStepBackward,
} from '@fortawesome/free-solid-svg-icons';
import {SingleProblem} from '@types';
import {useTranslation} from 'react-i18next';
import {
    CUSTOM_COMMENT_REGEX,
    getFirstSetupNodeFromRoot,
    getPlayerColorFromSgf,
    isExploringAfterSolution,
    useDispatch,
    useOffPath,
    useSelector,
} from '@utils';
import {globalBoardSettingsSlice, uiSlice} from '@reducers';
import {findProp, isRightNode} from 'ghostban';
import {isEmpty} from 'lodash';
import {useTheme} from '@components/theme/useTheme';
import {MarkdownContent} from '@components';

const DividerWithText = () => {
    return (
        <div className="flex items-center my-1">
            <hr className="flex-grow border-neutral-300" />
        </div>
    );
};

export const ProblemSolvingCard = forwardRef<
    HTMLDivElement,
    {
        problem: SingleProblem;
        onBack?: () => void;
        onNext?: () => Promise<void>;
        onReset?: () => void;
        commentsCount: number;
        compactLayout: boolean;
    } & React.HTMLAttributes<HTMLDivElement>
>(({problem, onBack, onNext, onReset, commentsCount, compactLayout}, ref) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {problemSolutionsCommentsVisible} = useSelector(state => state.ui);
    const {currentNode, root} = useSelector(state => state.board);

    const playerColor =
        problem.playerColor || getPlayerColorFromSgf(problem.sgf);
    const firstSetupNode = getFirstSetupNodeFromRoot(root);
    const [comment, setComment] = useState<string>('');
    const [isNextLoading, setIsNextLoading] = useState(false);
    const [offPath] = useOffPath(problem.sgf, currentNode);

    const isExploringSolution =
        currentNode && offPath
            ? isExploringAfterSolution(currentNode, problem.sgf)
            : false;

    const canBack = currentNode?.model?.id !== firstSetupNode?.model?.id;
    const canReset = currentNode?.model?.id !== firstSetupNode?.model?.id;

    const handleReset = () => {
        if (onReset) onReset();
    };

    const handleBack = () => {
        if (onBack) onBack();
    };

    const handleNext = async () => {
        if (!onNext || isNextLoading) return;

        setIsNextLoading(true);
        try {
            await onNext();
        } finally {
            setIsNextLoading(false);
        }
    };

    useEffect(() => {
        if (!currentNode) return;
        const commentNode = findProp(currentNode, 'C');
        const nodeComment = commentNode?.value || '';
        const filteredComment = nodeComment.replaceAll(
            CUSTOM_COMMENT_REGEX,
            ''
        );
        if (currentNode.isRoot() && isEmpty(filteredComment)) {
            setComment(problem.description);
            return;
        } else {
            setComment(filteredComment);
        }
    }, [currentNode, problem]);

    return (
        <>
            {compactLayout && (
                <div className="-mt-1 flex h-[34px] items-center justify-between">
                    <button
                        type="button"
                        className="relative inline-flex h-[34px] w-[108px] items-center justify-center gap-1 whitespace-nowrap rounded border-0 !border-r !border-black/10 bg-transparent px-2 text-xs font-medium text-neutral-600 transition-colors hover:bg-black/5 active:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:pointer-events-none disabled:text-neutral-300 dark:!border-white/10 dark:text-neutral-200 dark:hover:bg-white/10 dark:active:bg-white/[0.14] dark:disabled:text-neutral-600"
                        disabled={!onNext || isNextLoading}
                        onClick={() => void handleNext()}>
                        <span
                            className={isNextLoading ? 'invisible' : undefined}
                            aria-hidden={isNextLoading || undefined}>
                            {t('problem.next_problem')}
                        </span>
                        <FontAwesomeIcon
                            aria-hidden="true"
                            className={isNextLoading ? 'invisible' : 'text-xs'}
                            icon={faChevronRight}
                        />
                        {isNextLoading && (
                            <span className="absolute inset-0 flex items-center justify-center">
                                <Spinner size="sm" />
                            </span>
                        )}
                    </button>
                    <div
                        className="flex h-[34px] overflow-hidden rounded border-l border-black/10 bg-transparent divide-x divide-black/10 dark:border-white/10 dark:divide-white/10"
                        role="group"
                        aria-label={t('problem.board_navigation')}>
                        <button
                            type="button"
                            className="inline-flex min-w-[60px] items-center justify-center gap-1 px-2 text-xs font-medium text-neutral-600 transition-colors hover:bg-black/[0.08] active:bg-black/[0.12] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:cursor-default disabled:text-neutral-300 dark:text-neutral-300 dark:hover:bg-white/10 dark:active:bg-white/[0.14] dark:disabled:text-neutral-600"
                            disabled={!canBack}
                            onClick={handleBack}>
                            <FontAwesomeIcon
                                aria-hidden="true"
                                className="text-xs"
                                icon={faChevronLeft}
                            />
                            {t('problem.back', {defaultValue: 'Back'})}
                        </button>
                        <button
                            type="button"
                            className="inline-flex min-w-[60px] items-center justify-center gap-1 px-2 text-xs font-medium text-neutral-600 transition-colors hover:bg-black/[0.08] active:bg-black/[0.12] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:cursor-default disabled:text-neutral-300 dark:text-neutral-300 dark:hover:bg-white/10 dark:active:bg-white/[0.14] dark:disabled:text-neutral-600"
                            disabled={!canReset}
                            onClick={handleReset}>
                            <FontAwesomeIcon
                                aria-hidden="true"
                                className="text-xs"
                                icon={faRotateLeft}
                            />
                            {t('problem.reset')}
                        </button>
                    </div>
                </div>
            )}
            <Card
                ref={ref}
                style={{
                    width: compactLayout ? '100%' : 'min(29rem, 100%)',
                }}>
                <CardHeader className="!py-1 md:!py-2">
                    <div className="flex min-h-8 w-full items-center justify-between gap-2 md:min-h-0">
                        <div
                            className={
                                compactLayout
                                    ? 'hidden'
                                    : 'hidden items-center gap-1 md:flex'
                            }>
                            <button
                                type="button"
                                aria-label={t('problem.reset')}
                                disabled={!canReset}
                                onClick={handleReset}
                                className={`${canReset ? (theme === 'dark' ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-800') : theme === 'dark' ? 'text-neutral-600' : 'text-neutral-300'} inline-flex h-7 w-7 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-default`}>
                                <FontAwesomeIcon
                                    icon={faStepBackward}
                                    className="h-[22px] w-[22px]"
                                />
                            </button>
                            <button
                                type="button"
                                aria-label={t('single_problem.go_to_position', {
                                    defaultValue: 'Back',
                                })}
                                disabled={!canBack}
                                onClick={handleBack}
                                className={`${canBack ? (theme === 'dark' ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-800') : theme === 'dark' ? 'text-neutral-600' : 'text-neutral-300'} inline-flex h-7 w-7 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-default`}>
                                <FontAwesomeIcon
                                    icon={faPlay}
                                    rotation={180}
                                    className="h-[22px] w-[22px]"
                                />
                            </button>
                        </div>
                        <div className="flex gap-2 md:ml-4 md:text-sm items-center">
                            {playerColor === 'black' && (
                                <div className="rounded-full h-6 w-6 mr-1 md:mr-0 flex items-center justify-center bg-black"></div>
                            )}
                            {playerColor === 'white' && (
                                <div className="rounded-full h-6 w-6 mr-1 md:mr-0 bg-white border border-black"></div>
                            )}
                            <span className="capitalize font-bold">
                                {t('go.to_move')}
                            </span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                            {problemSolutionsCommentsVisible && compactLayout && (
                                <div
                                    className={`inline-flex items-center cursor-pointer px-2 ${theme === 'dark' ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-800'}`}
                                    onClick={() => {
                                        dispatch(
                                            uiSlice.actions.toggleProblemTreeVisible()
                                        );
                                    }}>
                                    <FontAwesomeIcon icon={faCodeBranch} />
                                </div>
                            )}
                            <div
                                className={`inline-flex items-center cursor-pointer px-2 ${theme === 'dark' ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-800'}`}
                                onClick={() => {
                                    dispatch(
                                        uiSlice.actions.toggleProblemSolutionsCommentsVisible()
                                    );
                                    if (problemSolutionsCommentsVisible) {
                                        dispatch(
                                            uiSlice.actions.setProblemTreeVisible(
                                                false
                                            )
                                        );
                                        if (onReset) onReset();
                                    }
                                    if (!problemSolutionsCommentsVisible) {
                                        dispatch(
                                            globalBoardSettingsSlice.actions.setGlobalBoardSettings(
                                                {coordinate: true}
                                            )
                                        );
                                    }
                                }}>
                                <span className="underline text-sm">
                                    {problemSolutionsCommentsVisible ? (
                                        <span>{t('problem.reset')}</span>
                                    ) : (
                                        <span>
                                            {`${t('problem.solutions')}/${t('problem.comments')} (${commentsCount})`}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="min-h-36 !p-2">
                    {isExploringSolution && (
                        <div className="rounded bg-neutral-100 px-2 text-neutral-600 border-neutral-300">
                            {t('single_problem.exploring_after_solution')}
                        </div>
                    )}
                    {offPath && !isExploringSolution && (
                        <div className="rounded bg-red-200 px-2 text-black border-red-400">
                            {t('single_problem.off_path')}
                        </div>
                    )}
                    {currentNode && isRightNode(currentNode) && (
                        <div className="rounded bg-lime-500 px-2 text-black border-lime-700">
                            {t('single_problem.right')}
                        </div>
                    )}
                    <div>
                        {currentNode?.isRoot() &&
                            !isEmpty(problem.description) &&
                            problem.description !== comment && (
                                <MarkdownContent text={problem.description} />
                            )}
                        {currentNode?.isRoot() &&
                            problem.description &&
                            problem.description !== comment &&
                            comment && <DividerWithText />}
                        <MarkdownContent text={comment} />
                    </div>
                </CardBody>
            </Card>
        </>
    );
});

ProblemSolvingCard.displayName = 'ProblemSolvingCard';
